import React, { useEffect, useRef, useState } from 'react';
import './SitePet.css';

export const PET_STORAGE_KEY = 'avm.site-pet';
export type PetVariant = 'none' | 'oneko' | 'roaming' | 'spider';

const SPRITE_SETS: Record<string, [number, number][]> = {
  idle: [[-3, -3]],
  alert: [[-7, -3]],
  scratchSelf: [[-5, 0], [-6, 0], [-7, 0]],
  scratchWallN: [[0, 0], [0, -1]],
  scratchWallS: [[-7, -1], [-6, -2]],
  scratchWallE: [[-2, -2], [-2, -3]],
  scratchWallW: [[-4, 0], [-4, -1]],
  tired: [[-3, -2]],
  sleeping: [[-2, 0], [-2, -1]],
  N: [[-1, -2], [-1, -3]],
  NE: [[0, -2], [0, -3]],
  E: [[-3, 0], [-3, -1]],
  SE: [[-5, -1], [-5, -2]],
  S: [[-6, -3], [-7, -2]],
  SW: [[-5, -3], [-6, -1]],
  W: [[-4, -2], [-4, -3]],
  NW: [[-2, -2], [-2, -3]],
};

const SPIDER_FRAME_COUNT = 12;
const SPIDER_FRAME_INTERVAL = 80; // ms per frame
const SPIDER_SIZE = 72;

function setSprite(el: HTMLDivElement, name: string, frame: number) {
  const set = SPRITE_SETS[name];
  if (!set) return;
  const [col, row] = set[frame % set.length];
  el.style.backgroundPosition = `${col * 32}px ${row * 32}px`;
}

function computeDirection(diffX: number, diffY: number, distance: number): string {
  let dir = '';
  if (diffY / distance > 0.5) dir += 'N';
  if (diffY / distance < -0.5) dir += 'S';
  if (diffX / distance > 0.5) dir += 'W';
  if (diffX / distance < -0.5) dir += 'E';
  return dir;
}

// --- Cursor-following mode (oneko) ---
function runOneko(el: HTMLDivElement): () => void {
  const NEKO_SPEED = 10;

  let nekoX = window.innerWidth / 2;
  let nekoY = window.innerHeight / 2;
  let mouseX = nekoX;
  let mouseY = nekoY;
  let frame = 0;
  let idleTime = 0;
  let idleAnimation: string | null = null;
  let idleFrame = 0;

  el.style.left = `${nekoX - 16}px`;
  el.style.top = `${nekoY - 16}px`;

  const onMouseMove = (e: MouseEvent) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
  };
  document.addEventListener('mousemove', onMouseMove);

  let rafId: number;

  function tick() {
    rafId = requestAnimationFrame(tick);

    const diffX = nekoX - mouseX;
    const diffY = nekoY - mouseY;
    const distance = Math.sqrt(diffX * diffX + diffY * diffY);

    if (distance < NEKO_SPEED || distance < 48) {
      idleTime++;

      if (idleTime > 10 && !idleAnimation) {
        const pool = ['sleeping', 'scratchSelf'];
        if (nekoY < 32) pool.push('scratchWallN');
        if (nekoX < 32) pool.push('scratchWallW');
        if (nekoX > window.innerWidth - 32) pool.push('scratchWallE');
        if (nekoY > window.innerHeight - 32) pool.push('scratchWallS');
        idleAnimation = pool[Math.floor(Math.random() * pool.length)];
        idleFrame = 0;
      }

      if (idleAnimation) {
        if (idleAnimation === 'sleeping') {
          setSprite(el, idleFrame < 8 ? 'tired' : 'sleeping', Math.floor(idleFrame / 4));
        } else {
          setSprite(el, idleAnimation, idleFrame);
        }
        idleFrame++;
      } else {
        setSprite(el, 'idle', 0);
      }
      return;
    }

    if (idleTime > 1) {
      setSprite(el, 'alert', 0);
      idleTime = Math.min(idleTime, 7);
      idleTime--;
      return;
    }

    idleAnimation = null;
    idleFrame = 0;
    idleTime = 0;

    const dir = computeDirection(diffX, diffY, distance);
    setSprite(el, dir || 'idle', Math.floor(frame / 2) % 2);

    nekoX -= (diffX / distance) * NEKO_SPEED;
    nekoY -= (diffY / distance) * NEKO_SPEED;
    nekoX = Math.min(Math.max(16, nekoX), window.innerWidth - 16);
    nekoY = Math.min(Math.max(16, nekoY), window.innerHeight - 16);

    el.style.left = `${nekoX - 16}px`;
    el.style.top = `${nekoY - 16}px`;
    frame = (frame + 1) % 100;
  }

  rafId = requestAnimationFrame(tick);

  return () => {
    cancelAnimationFrame(rafId);
    document.removeEventListener('mousemove', onMouseMove);
  };
}

// --- Roaming mode (wanders around the page) ---
function runRoaming(el: HTMLDivElement): () => void {
  const SPEED = 1.5;
  const STOP_DIST = 8;

  let x = window.innerWidth / 2;
  let y = window.innerHeight / 2;
  let targetX = x;
  let targetY = y;
  let frame = 0;
  let idleTime = 0;
  let idleAnimation: string | null = null;
  let idleFrame = 0;

  el.style.left = `${x - 16}px`;
  el.style.top = `${y - 16}px`;

  function pickTarget() {
    const m = 64;
    targetX = m + Math.random() * (window.innerWidth - m * 2);
    targetY = m + Math.random() * (window.innerHeight - m * 2);
  }

  pickTarget();
  const walkTimer = setInterval(pickTarget, 5000 + Math.random() * 5000);

  let rafId: number;

  function tick() {
    rafId = requestAnimationFrame(tick);

    const diffX = x - targetX;
    const diffY = y - targetY;
    const distance = Math.sqrt(diffX * diffX + diffY * diffY);

    if (distance < STOP_DIST) {
      idleTime++;

      if (idleTime > 40 && !idleAnimation) {
        const pool = ['sleeping', 'scratchSelf', 'idle'];
        if (y < 48) pool.push('scratchWallN');
        if (x < 48) pool.push('scratchWallW');
        if (x > window.innerWidth - 48) pool.push('scratchWallE');
        if (y > window.innerHeight - 48) pool.push('scratchWallS');
        idleAnimation = pool[Math.floor(Math.random() * pool.length)];
        idleFrame = 0;
      }

      if (idleAnimation) {
        if (idleAnimation === 'sleeping') {
          setSprite(el, idleFrame < 8 ? 'tired' : 'sleeping', Math.floor(idleFrame / 4));
        } else if (idleAnimation === 'idle') {
          setSprite(el, 'idle', 0);
        } else {
          setSprite(el, idleAnimation, idleFrame);
        }
        idleFrame++;
      } else {
        setSprite(el, 'idle', 0);
      }
      return;
    }

    idleAnimation = null;
    idleFrame = 0;
    idleTime = 0;

    const dir = computeDirection(diffX, diffY, distance);
    setSprite(el, dir || 'idle', Math.floor(frame / 2) % 2);

    x -= (diffX / distance) * SPEED;
    y -= (diffY / distance) * SPEED;
    x = Math.min(Math.max(16, x), window.innerWidth - 16);
    y = Math.min(Math.max(16, y), window.innerHeight - 16);

    el.style.left = `${x - 16}px`;
    el.style.top = `${y - 16}px`;
    frame = (frame + 1) % 100;
  }

  rafId = requestAnimationFrame(tick);

  return () => {
    cancelAnimationFrame(rafId);
    clearInterval(walkTimer);
  };
}

// --- Spider mode (follows cursor, cycles through spideko frames) ---
function runSpider(el: HTMLDivElement): () => void {
  const SPEED = 4;
  const HALF = SPIDER_SIZE / 2;

  let x = window.innerWidth / 2;
  let y = window.innerHeight / 2;
  let mouseX = x;
  let mouseY = y;
  let frameIndex = 0;
  let lastFrameTs = 0;

  el.style.width = `${SPIDER_SIZE}px`;
  el.style.height = `${SPIDER_SIZE}px`;
  el.style.backgroundSize = 'contain';
  el.style.left = `${x - HALF}px`;
  el.style.top = `${y - HALF}px`;
  el.style.backgroundImage = `url(${process.env.PUBLIC_URL}/spider_anim/frame_00.gif)`;

  const onMouseMove = (e: MouseEvent) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
  };
  document.addEventListener('mousemove', onMouseMove);

  let rafId: number;

  function tick(ts: number) {
    rafId = requestAnimationFrame(tick);

    // Advance animation frame
    if (ts - lastFrameTs > SPIDER_FRAME_INTERVAL) {
      frameIndex = (frameIndex + 1) % SPIDER_FRAME_COUNT;
      const num = String(frameIndex).padStart(2, '0');
      el.style.backgroundImage = `url(${process.env.PUBLIC_URL}/spider_anim/frame_${num}.gif)`;
      lastFrameTs = ts;
    }

    const diffX = x - mouseX;
    const diffY = y - mouseY;
    const distance = Math.sqrt(diffX * diffX + diffY * diffY);

    if (distance < HALF) return;

    x -= (diffX / distance) * SPEED;
    y -= (diffY / distance) * SPEED;
    x = Math.min(Math.max(HALF, x), window.innerWidth - HALF);
    y = Math.min(Math.max(HALF, y), window.innerHeight - HALF);

    el.style.left = `${x - HALF}px`;
    el.style.top = `${y - HALF}px`;
  }

  rafId = requestAnimationFrame(tick);

  return () => {
    cancelAnimationFrame(rafId);
    document.removeEventListener('mousemove', onMouseMove);
  };
}

// --- Widget wrappers ---
function CatWidget({ mode }: { mode: 'oneko' | 'roaming' }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    return mode === 'oneko' ? runOneko(ref.current) : runRoaming(ref.current);
  }, [mode]);

  return (
    <div
      ref={ref}
      className="site-pet"
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/oneko.gif)` }}
      aria-hidden="true"
    />
  );
}

function SpiderWidget() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    return runSpider(ref.current);
  }, []);

  return <div ref={ref} className="site-pet" aria-hidden="true" />;
}

// --- Public component ---
const SitePet: React.FC = () => {
  const [variant, setVariant] = useState<PetVariant>(
    () => (localStorage.getItem(PET_STORAGE_KEY) as PetVariant) ?? 'none'
  );

  useEffect(() => {
    const sync = () =>
      setVariant((localStorage.getItem(PET_STORAGE_KEY) as PetVariant) ?? 'none');
    window.addEventListener('avm.site-pet-changed', sync);
    return () => window.removeEventListener('avm.site-pet-changed', sync);
  }, []);

  if (variant === 'oneko') return <CatWidget mode="oneko" />;
  if (variant === 'roaming') return <CatWidget mode="roaming" />;
  if (variant === 'spider') return <SpiderWidget />;
  return null;
};

export default SitePet;
